/**
 * PhotoDetailPage
 *
 * Thin orchestration layer for photo detail view.
 * Supports both old route pattern (/:username/photos/:slug) and new patterns:
 * - /p/:photoId (standalone)
 * - /u/:username/photos/:photoId (user-scoped)
 *
 * All business logic and data fetching handled by composed components.
 */

import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { usePhotoByIdGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import { isValidGlobalId, userPhotoUrl } from '@/utils/urls';
import { LoadingState, ErrorState } from '@/components/composed';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import { Photo } from '@/types/photo';

export function PhotoDetailPage() {
  const { username, photoId } = useParams<{
    username?: string;
    photoId?: string;
  }>();

  // Validate global ID format
  if (!photoId || !isValidGlobalId(photoId)) {
    return (
      <ErrorState
        title="Invalid Photo ID"
        message="The photo ID format is invalid. Please check the URL and try again."
      />
    );
  }

  // Fetch photo by global ID using GraphQL
  const { data: photo, isLoading, error } = usePhotoByIdGraphQL(photoId);

  // Update URL bar if wrong username provided in user-scoped route
  useEffect(() => {
    if (photo && username && photoId) {
      const photoOwner = (photo as any).owner;
      if (photoOwner?.slug && photoOwner.slug !== username) {
        // Wrong username in URL - update to correct username without page reload
        window.history.replaceState(null, '', userPhotoUrl(photoOwner.slug, photoId));
      }
    }
  }, [photo, username, photoId]);

  if (isLoading) {
    return <LoadingState message="Loading photo..." size="lg" />;
  }

  if (error || !photo) {
    return (
      <ErrorState
        title="Photo Not Found"
        message="The photo you're looking for doesn't exist or has been removed."
      />
    );
  }

  // Render photo directly with AdaptivePhoto
  return (
    <div className="w-full h-full flex items-center justify-center p-4">
      <AdaptivePhoto
        photo={photo as Photo}
        alt={photo.title || photo.description || 'Photo'}
        className="w-full max-w-7xl"
        imgClassName="w-full h-auto object-contain"
        trackingSource="photo_detail"
      />
    </div>
  );
}
